import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';

const ViewerForm = ({ 
  viewerFormOpen, 
  formData, 
  setFormData, 
  handleViewerFormSubmit, 
  formError, 
  company, 
  isAdmin, 
  handleAdminView 
}) => {
  return (
    <Transition appear show={viewerFormOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-500"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-xl" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-500"
              enterFrom="opacity-0 scale-90 translate-y-8"
              enterTo="opacity-100 scale-100 translate-y-0"
              leave="ease-in duration-300"
              leaveFrom="opacity-100 scale-100 translate-y-0"
              leaveTo="opacity-0 scale-95 translate-y-4"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-3xl bg-white shadow-2xl shadow-black/25 transition-all border border-gray-100">
                {/* Clean gradient header accent */}
                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-purple-500 via-violet-500 to-indigo-500" />
                
                <div className="relative p-8 bg-white">
                  {/* Subtle radial gradient overlay - positioned lower */}
                  <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-purple-50/30 pointer-events-none" />
                  
                  <div className="relative">
                    {/* Enhanced icon with better shadow */}
                    <div className="mx-auto mb-6 w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center shadow-lg shadow-purple-500/25">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>

                    <Dialog.Title as="h3" className="text-3xl font-bold bg-gradient-to-r from-gray-900 via-purple-900 to-violet-900 bg-clip-text text-transparent mb-3 text-center tracking-tight">
                      Welcome to {company.name}
                    </Dialog.Title>
                    
                    <p className="text-gray-600 text-center mb-8 font-medium text-lg leading-relaxed">
                      Join us on an exclusive journey through our vision
                    </p>
                    
                    <form onSubmit={handleViewerFormSubmit} className="space-y-6">
                      <div className="space-y-6">
                        <div className="group">
                          <label htmlFor="email" className="block text-sm font-semibold text-gray-800 mb-3 text-left">
                            Email Address
                          </label>
                          <div className="relative">
                            <input
                              type="email"
                              id="email"
                              value={formData.email}
                              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                              className="w-full px-5 py-4 rounded-2xl border-2 border-gray-200 bg-gray-50/50 text-gray-900 placeholder-gray-500 transition-all duration-300 focus:border-purple-400 focus:ring-4 focus:ring-purple-100 focus:bg-white group-hover:border-gray-300 group-hover:bg-white/80 text-base"
                              placeholder="your@email.com"
                              required
                            />
                            <div className="absolute inset-y-0 right-0 flex items-center pr-5">
                              <svg className="w-5 h-5 text-gray-400 group-focus-within:text-purple-500 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                              </svg>
                            </div>
                          </div>
                        </div>
                        
                        <div className="group">
                          <label htmlFor="fullName" className="block text-sm font-semibold text-gray-800 mb-3 text-left">
                            Full Name
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              id="fullName"
                              value={formData.fullName}
                              onChange={(e) => setFormData(prev => ({ ...prev, fullName: e.target.value }))}
                              className="w-full px-5 py-4 rounded-2xl border-2 border-gray-200 bg-gray-50/50 text-gray-900 placeholder-gray-500 transition-all duration-300 focus:border-purple-400 focus:ring-4 focus:ring-purple-100 focus:bg-white group-hover:border-gray-300 group-hover:bg-white/80 text-base"
                              placeholder="Your full name"
                              required
                            />
                            <div className="absolute inset-y-0 right-0 flex items-center pr-5">
                              <svg className="w-5 h-5 text-gray-400 group-focus-within:text-purple-500 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>

                      {formError && (
                        <div className="flex items-center space-x-3 text-red-600 bg-red-50 px-5 py-4 rounded-xl border border-red-200/80 shadow-sm">
                          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-sm font-medium">{formError}</span>
                        </div>
                      )}

                      <div className="pt-2 space-y-3">
                        <button
                          type="submit"
                          className="group relative w-full overflow-hidden rounded-xl bg-gradient-to-r from-purple-600 via-violet-600 to-indigo-600 px-6 py-4 text-base font-semibold text-white shadow-lg shadow-purple-500/25 transition-all duration-300 hover:shadow-xl hover:shadow-purple-500/40 hover:scale-[1.02] focus:outline-none focus:ring-4 focus:ring-purple-100 active:scale-[0.98]"
                        >
                          <span className="relative z-10 flex items-center justify-center space-x-2">
                            <span>Continue to Pitch Deck</span>
                            <svg className="w-5 h-5 transform transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                            </svg>
                          </span>
                          
                          {/* Animated background */}
                          <div className="absolute inset-0 bg-gradient-to-r from-purple-700 via-violet-700 to-indigo-700 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
                        </button>

                        {isAdmin && (
                          <button
                            type="button"
                            onClick={handleAdminView}
                            className="w-full px-6 py-4 text-base font-semibold text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors duration-200 focus:outline-none focus:ring-4 focus:ring-gray-100"
                          >
                            Continue as Admin
                          </button>
                        )}
                      </div>
                    </form>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ViewerForm; 